import {
    MATERIAL_SUMMARY_DESTINATION_FACILITY_ELEMENT_ID,
    MATERIAL_SUMMARY_FROM_DATE_ELEMENT_ID,
    MATERIAL_SUMMARY_INPUT_MATERIAL_TYPE_ID_ELEMENT_ID,
    MATERIAL_SUMMARY_MATERIAL_TYPE_NAME_ELEMENT_ID,
    MATERIAL_SUMMARY_ORIGIN_FACILITY_ELEMENT_ID,
    MATERIAL_SUMMARY_RECORD_COUNT_DEFAULT_VALUE,
    MATERIAL_SUMMARY_RECORD_COUNT_SELECT_OPTIONS,
    MATERIAL_SUMMARY_REPORT_ALIAS_ELEMENT_ID,
    MATERIAL_SUMMARY_REPORT_DATE_ELEMENT_ID,
    MATERIAL_SUMMARY_TO_DATE_ELEMENT_ID,
} from '../../../material-summary/constants/material-summary.constants';
import { REPORT_RECORD_COUNT_ELEMENT_ID } from '../../../agreements/report-record-count.constants';
import type {
    ReportFilterFieldSpec,
    ReportingTargetFilterDefinition,
} from '../reporting-target-filter.types';

const MS_DATE_SINGLE_ROW = 'material-summary-date-single';
const MS_DATE_RANGE_ROW = 'material-summary-date-range';

const MATERIAL_SUMMARY_RECORD_TYPE_OPTIONS = [
    { value: '', label: 'All types' },
    { value: 'receipt', label: 'Receipt' },
    { value: 'dispatched', label: 'Dispatched' },
    { value: 'processed', label: 'Processed' },
] as const;

const MATERIAL_SUMMARY_DATE_HELPER =
    'Use either a single report date or a from/to range — not both. If you set only from date, to date defaults to today. Dates must be YYYY-MM-DD.';

/** All Get-Material-Summary filter fields (declarative UI). */
const MATERIAL_SUMMARY_TARGET_FIELDS: readonly ReportFilterFieldSpec[] = [
    {
        elementId: REPORT_RECORD_COUNT_ELEMENT_ID,
        label: 'Record count',
        kind: 'select',
        defaultValue: MATERIAL_SUMMARY_RECORD_COUNT_DEFAULT_VALUE,
        options: MATERIAL_SUMMARY_RECORD_COUNT_SELECT_OPTIONS,
    },
    {
        elementId: MATERIAL_SUMMARY_REPORT_DATE_ELEMENT_ID,
        label: 'Report date (single day)',
        kind: 'date',
        optional: true,
        rowGroup: MS_DATE_SINGLE_ROW,
        helperText: MATERIAL_SUMMARY_DATE_HELPER,
    },
    {
        elementId: MATERIAL_SUMMARY_FROM_DATE_ELEMENT_ID,
        label: 'From date',
        kind: 'date',
        optional: true,
        rowGroup: MS_DATE_RANGE_ROW,
        helperText: MATERIAL_SUMMARY_DATE_HELPER,
    },
    {
        elementId: MATERIAL_SUMMARY_TO_DATE_ELEMENT_ID,
        label: 'To date',
        kind: 'date',
        optional: true,
        rowGroup: MS_DATE_RANGE_ROW,
        helperText: 'Defaults to today when using a date range.',
    },
    {
        elementId: MATERIAL_SUMMARY_ORIGIN_FACILITY_ELEMENT_ID,
        label: 'Origin facility',
        kind: 'select',
        optional: true,
        placeholder: 'Select facility',
        options: [],
    },
    {
        elementId: MATERIAL_SUMMARY_DESTINATION_FACILITY_ELEMENT_ID,
        label: 'Destination facility',
        kind: 'select',
        optional: true,
        placeholder: 'Select facility',
        options: [],
    },
    {
        elementId: MATERIAL_SUMMARY_MATERIAL_TYPE_NAME_ELEMENT_ID,
        label: 'Material type name',
        kind: 'text',
        optional: true,
        placeholder: 'Contains filter',
    },
    {
        elementId: MATERIAL_SUMMARY_INPUT_MATERIAL_TYPE_ID_ELEMENT_ID,
        label: 'Input material type id',
        kind: 'text',
        optional: true,
        placeholder: 'Guid or id',
    },
    {
        elementId: MATERIAL_SUMMARY_REPORT_ALIAS_ELEMENT_ID,
        label: 'Report alias',
        kind: 'text',
        optional: true,
        placeholder: 'Alias',
    },
];

/**
 * Reporting target: Material Summary — must match `<option value="...">` in `taskpane.html`.
 */
export const materialSummaryReportingTargetDefinition: ReportingTargetFilterDefinition = {
    targetId: 'material-summary',
    displayName: 'Material Summary',
    fetchImplemented: true,
    commonFilterFields: [],
    targetFilterFields: MATERIAL_SUMMARY_TARGET_FIELDS,
    recordTypeFilter: {
        options: [...MATERIAL_SUMMARY_RECORD_TYPE_OPTIONS],
        defaultValue: '',
        payloadBinding: 'recordType',
    },
};
